/**
 * Emoji message store — transient UI state for floating emoji bubbles.
 *
 * Messages auto-remove after DISPLAY_DURATION_MS.
 * Uses useSyncExternalStore pattern (same as ws-store, lobby-store).
 */

import { useSyncExternalStore } from "react";
import type { EmojiId, EmojiMessageEvent } from "./types";
import { getEmojiEntry } from "./types";

const DISPLAY_DURATION_MS = 3_000;
const COOLDOWN_MS = 3_000;
const MAX_ACTIVE_MESSAGES = 8;

interface EmojiMessageState {
  readonly activeMessages: ReadonlyArray<EmojiMessageEvent>;
  readonly cooldownUntil: number;
}

let state: EmojiMessageState = {
  activeMessages: [],
  cooldownUntil: 0,
};

const listeners = new Set<() => void>();
const removalTimers = new Map<string, ReturnType<typeof setTimeout>>();

function notify(): void {
  for (const listener of listeners) {
    listener();
  }
}

let messageCounter = 0;

function generateId(): string {
  messageCounter += 1;
  return `emoji-${Date.now()}-${messageCounter}`;
}

export const emojiMessageActions = {
  addMessage(userId: string, emojiId: EmojiId, displayName: string): void {
    const entry = getEmojiEntry(emojiId);
    if (!entry) return;

    const id = generateId();
    const message: EmojiMessageEvent = {
      id,
      userId,
      displayName,
      emojiId,
      emoji: entry.emoji,
      timestamp: Date.now(),
    };

    const appended = [...state.activeMessages, message];
    const evicted =
      appended.length > MAX_ACTIVE_MESSAGES
        ? appended.slice(appended.length - MAX_ACTIVE_MESSAGES)
        : appended;

    // Clean up timers for evicted messages
    for (const msg of state.activeMessages) {
      if (!evicted.includes(msg)) {
        const orphanTimer = removalTimers.get(msg.id);
        if (orphanTimer) {
          clearTimeout(orphanTimer);
          removalTimers.delete(msg.id);
        }
      }
    }

    state = { ...state, activeMessages: evicted };
    notify();

    const timer = setTimeout(() => {
      emojiMessageActions.removeMessage(id);
      removalTimers.delete(id);
    }, DISPLAY_DURATION_MS);
    removalTimers.set(id, timer);
  },

  removeMessage(id: string): void {
    state = {
      ...state,
      activeMessages: state.activeMessages.filter((m) => m.id !== id),
    };
    notify();
  },

  startCooldown(): void {
    state = {
      ...state,
      cooldownUntil: Date.now() + COOLDOWN_MS,
    };
    notify();
  },

  clearAll(): void {
    for (const timer of removalTimers.values()) {
      clearTimeout(timer);
    }
    removalTimers.clear();
    state = { activeMessages: [], cooldownUntil: 0 };
    notify();
  },
};

function getState(): EmojiMessageState {
  return state;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useEmojiMessages(): EmojiMessageState {
  return useSyncExternalStore(subscribe, getState, getState);
}
